import { Box, Button, Dialog, Typography } from "@mui/material";
import { useTranslation } from "react-i18next";

import OnboardingAvatar from "./OnboardingAvatar";

interface OnboardingWelcomeProps {
  onStart: () => void;
  onDismiss: () => void;
}

const AVATAR_SIZE = 56;

export default function OnboardingWelcome({
  onStart,
  onDismiss,
}: OnboardingWelcomeProps) {
  const { t } = useTranslation();

  return (
    <Dialog
      open
      aria-labelledby="onboarding-welcome-title"
      aria-describedby="onboarding-welcome-body"
      data-testid="onboarding-welcome-dialog"
      maxWidth={false}
      slotProps={{
        backdrop: {
          sx: { backgroundColor: "rgba(39, 45, 55, 0.45)" },
        },
      }}
      PaperProps={{
        sx: {
          width: 400,
          maxWidth: "calc(100vw / var(--app-zoom, 1) - 32px)",
          borderRadius: "12px",
          backgroundColor: "var(--pi-surface)",
          backgroundImage: "none",
          boxShadow: "0 16px 40px rgba(39, 45, 55, 0.18)",
        },
      }}
    >
      <Box sx={{ pt: "32px", px: "28px", pb: "28px", textAlign: "center" }}>
        <Box sx={{ display: "flex", justifyContent: "center", mb: "18px" }}>
          <OnboardingAvatar size={AVATAR_SIZE} />
        </Box>

        <Typography
          id="onboarding-welcome-title"
          variant="h5"
          component="h1"
          sx={{
            fontSize: 20,
            fontWeight: 700,
            lineHeight: 1.25,
            letterSpacing: "-0.02em",
            color: "var(--pi-text-color-1)",
          }}
        >
          {t("onboarding.welcome.title")}
        </Typography>

        <Typography
          id="onboarding-welcome-body"
          sx={{
            mt: "10px",
            // Keeps the two-line body from stretching the full width and
            // reading as a ribbon.
            maxWidth: 304,
            mx: "auto",
            fontSize: 14,
            lineHeight: 1.6,
            color: "var(--pi-menu-text-color-1)",
          }}
        >
          {t("onboarding.welcome.body")}
        </Typography>

        <Box sx={{ mt: "24px", display: "flex", gap: "12px" }}>
          {/**
           * Both buttons take an equal half. The piBlue variant is width: 100%
           * by default, so leaving these to size themselves let the primary
           * eat the row and wrapped "Not now" onto two lines.
           */}
          <Button
            variant="piBlueOutlined"
            onClick={onDismiss}
            data-testid="onboarding-welcome-not-now"
            sx={{ flex: 1, height: 40, whiteSpace: "nowrap", boxShadow: "none" }}
          >
            {t("onboarding.welcome.not_now")}
          </Button>
          <Button
            variant="piBlue"
            onClick={onStart}
            autoFocus
            disableRipple
            disableElevation
            data-testid="onboarding-welcome-start"
            sx={{
              flex: 1,
              height: 40,
              whiteSpace: "nowrap",
              boxShadow: "none",
              "&:hover": { boxShadow: "none" },
              "&.Mui-focusVisible": {
                outline: "2px solid var(--pi-key-blue)",
                outlineOffset: "2px",
              },
            }}
          >
            {t("onboarding.welcome.start")}
          </Button>
        </Box>
      </Box>
    </Dialog>
  );
}
